import '~/stylesheets/base/_nav-bar.scss';
import '~/stylesheets/shared/animation.scss';
import Arrow from '~/images/arrow-right-yellow-circle.svg';
import JerryLogo from '~/images/jerrycan-logo.svg';
import UserAccessSection from '~/javascripts/components/Nav/UserAccessSection';
import WaterDroplet from '~/images/icons/water-droplet-with-outline.svg';
import store from '~/javascripts/store.js';
import { isUkSite, usOrUkUrlFor } from '~/javascripts/utils/localization';

const NavBar = ({
  avatarUrl,
  currentUserPresent,
  profileDonateButtonText,
  profileOwnerFullName,
  signInParams,
  teams,
  userEmail,
  userFullName,
  userHasOpenCampaigns,
  userSlug,
  userViewingOtherProfile,
}) => {
  const lastVisibleDonateFormOnPage = () => {
    const allDonateForms = document.querySelectorAll(
      '.donate-form-wrapper form'
    );

    return Array.from(allDonateForms).findLast(
      (form) => form.offsetParent !== null
    );
  };

  const scrollToAndFlashElement = (el) => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    el.classList.add('flash-element');
    el.addEventListener(
      'animationend',
      () => {
        el.classList.remove('flash-element');
      },
      { once: true }
    );
  };

  const redirectToDonatePage = () => {
    window.location.href = usOrUkUrlFor('/donate');
  };

  const handleGiveButtonClick = () => {
    if (window.isMobile) {
      redirectToDonatePage();
      return;
    }

    const visibleDonateForm = lastVisibleDonateFormOnPage();

    if (visibleDonateForm) {
      scrollToAndFlashElement(visibleDonateForm);
    } else {
      redirectToDonatePage();
    }
  };

  const donateButton = () =>
    userViewingOtherProfile ? (
      <a
        className='button button--small button--cw-navy whitespace-nowrap'
        href='#donate'
      >
        {profileDonateButtonText}
      </a>
    ) : (
      <a
        className='button button--small cw-button flex items-center p-2'
        onClick={handleGiveButtonClick}
      >
        <div className='heading-xs'>Give</div>
        <img className='ml-2' src={WaterDroplet} />
      </a>
    );

  return (
    <section className='theme-design-system-v1 nav-bar-section flex justify-between items-center'>
      <div className='left-side flex'>
        <ul className='mobile-nav-items flex items-center lg-hidden'>
          <a
            className='offcanvas-open-link off-canvas-toggle icn cw-icon-reorder-thin mr-6'
            onClick={() => store.nav.toggleOffCanvas()}
          />
          {!userViewingOtherProfile && (
            <a href={usOrUkUrlFor('/')}>
              <img className='md-hidden' src={JerryLogo} />
            </a>
          )}
        </ul>

        {!userViewingOtherProfile ? (
          <>
            <ul className='logo-area hidden md-block'>
              <a href={usOrUkUrlFor('/')}>
                <li className='logo' />
              </a>
            </ul>

            <ul className='nav-items'>
              <li className='has-dropdown'>
                <span className='nav-hover-extender absolute bg-transparent z-10' />
                <span className='flex items-center dropdown-parent-link'>
                  <a
                    className='heading-xs transition-opacity relative z-20 mr-4'
                    href={usOrUkUrlFor('/get-involved')}
                  >
                    Take Action
                  </a>
                  <span className='h80 icn cw-icon-chevron-down' />
                </span>
                <span className='dropdown leading-normal left-0 rounded'>
                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href={usOrUkUrlFor('/donate/the-spring')}
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            Give monthly
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Join the global community of supporters serving people
                          with clean water every&nbsp;month.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href={usOrUkUrlFor('/fundraise')}
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            Fundraise for clean water
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Support clean water projects by raising awareness and
                          funds for people in&nbsp;need.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href={usOrUkUrlFor('/donate/water-project-sponsorship')}
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            Become a water project sponsor
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Transform an entire community or school with a gift of
                          {isUkSite() ? ' £8,500 ' : ' $10,000 '} or&nbsp;more.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href={usOrUkUrlFor('/partnerships')}
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            Become a brand partner
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Work closely with our team to develop mutually
                          beneficial initiatives with undeniable&nbsp;impact.
                        </div>
                      </div>
                    </a>
                  </div>

                  {!isUkSite() && (
                    <div className='dropdown-child'>
                      <a
                        className='text-cw-brand-grey-900 no-underline'
                        href='/the-tributary'
                      >
                        <div className='px-3 py-5'>
                          <div className='header flex items-center'>
                            <div className='text-sm font-medium'>
                              Plan legacy giving
                            </div>
                            <img className='ml-3' src={Arrow} />
                          </div>
                          <div className='dropdown-child-description body-s mt-2 font-normal'>
                            Make a lasting gift to help ensure that every person
                            of the planet has access to clean&nbsp;water.
                          </div>
                        </div>
                      </a>
                    </div>
                  )}

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href='/experience'
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            Visit the Experience Lab
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Explore humanity’s search for water through virtual
                          reality, holograms, and immersive video.
                        </div>
                      </div>
                    </a>
                  </div>
                </span>
              </li>

              <li className='has-dropdown'>
                <span className='nav-hover-extender absolute bg-transparent z-10' />
                <span className='flex items-center dropdown-parent-link'>
                  <a
                    className='heading-xs transition-opacity relative z-20 mr-4'
                    href={usOrUkUrlFor('/about')}
                  >
                    About Us
                  </a>
                  <span className='h80 icn cw-icon-chevron-down' />
                </span>
                <span className='dropdown leading-normal left-0 rounded'>
                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href='/our-approach/100-percent-model'
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            The 100% model
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Private donors cover our operating costs so 100% of
                          every public donation can fund clean&nbsp;water.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href={usOrUkUrlFor('/our-work')}
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>Our work</div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          See our funded water projects and proof
                          of&nbsp;impact.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href={usOrUkUrlFor('/how-we-work')}
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>How we work</div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Learn about how we build long-term sustainable
                          water&nbsp;projects.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href='/about/scott-harrison-story'
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            Meet the founder
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Learn how a radical decision to reset his life led
                          Scott Harrison to build charity:&nbsp;water.
                        </div>
                      </div>
                    </a>
                  </div>

                  <div className='dropdown-child'>
                    <a
                      className='text-cw-brand-grey-900 no-underline'
                      href='/about/financials'
                    >
                      <div className='px-3 py-5'>
                        <div className='header flex items-center'>
                          <div className='text-sm font-medium'>
                            See our financials
                          </div>
                          <img className='ml-3' src={Arrow} />
                        </div>
                        <div className='dropdown-child-description body-s mt-2 font-normal'>
                          Learn about how your gift is being used to bring clean
                          and safe water to people in&nbsp;need.
                        </div>
                      </div>
                    </a>
                  </div>
                </span>
              </li>

              <li className='no-dropdown'>
                <div className='header flex h-full items-center'>
                  <a
                    className='heading-xs'
                    href={usOrUkUrlFor('/global-water-crisis')}
                  >
                    Why Water?
                  </a>
                </div>
              </li>
            </ul>
          </>
        ) : (
          <ul className='nav-items flex items-center'>
            <li className='ml-0'>
              <div className='h80 font-medium'>
                <a href={usOrUkUrlFor('/')}>
                  <img className='' src={JerryLogo} />
                </a>
              </div>
            </li>
            <li className='ml-4'>
              <div className='h80 font-medium'>
                <i className='icn cw-icon cw-icon-angle-close' />
              </div>
            </li>
            <li className='ml-4'>
              <div className='h80 font-medium uppercase tracking-wide'>
                {profileOwnerFullName}
              </div>
            </li>
          </ul>
        )}
      </div>

      <div className='right-side items-center ml-2'>
        <UserAccessSection
          avatarUrl={avatarUrl}
          currentUserPresent={currentUserPresent}
          signInParams={signInParams}
          teams={teams}
          userEmail={userEmail}
          userFullName={userFullName}
          userHasOpenCampaigns={userHasOpenCampaigns}
          userSlug={userSlug}
        />

        <ul className='donate-link items-center'>
          <li className='button-links relative pl-5 ml-5 md-pl-8 md-ml-8'>
            {donateButton()}
          </li>
        </ul>
      </div>
    </section>
  );
};

export default NavBar;
